import * as React from 'react';
import { useState, createContext, useEffect } from 'react';
import moment from 'moment';
import {  isEqual } from "lodash";
import {filterPolicyMessages} from '../form/helper';

import { TextField }  from "~common/components/fields/text-field-fc";
import { QuestionField }  from "~common/components/fields/question-field-fc";
import { DateField }  from "~common/components/fields/date-field-fc";
import { DateRangeField }  from "~common/components/fields/date-range-fc";
import { TimeRangeField }  from "~common/components/fields/time-range-fc";
import { AddressField }  from "~common/components/fields/address-field-fc";
import { TimeField }  from "~common/components/fields/time-field-fc";
import { NumberField }  from "~common/components/fields/number-field-fc";
import { NumberRangeField }  from "~common/components/fields/number-range-fc";
import { ValidationField }  from "~common/components/fields/validation-field-fc";
import { CheckboxField }  from "~common/components/fields/checkbox-field-fc";



import BooleanField from "~common/components/fields/boolean-field-fc";
import AutocompleteField from './fields/autocomplete-field-fc';

import VersionedDocumentField from "~common/components/fields/versioned-document-field-fc";
import VersionedDocumentListField from "~common/components/fields/versioned-document-list-fc";
import ImageList from "~common/components/fields/image-list-fc";

import RichTextField from "~common/components/fields/rich-text-fc";

import JsonField from "~common/components/fields/json-field-fc";

import {registerJsonLabel} from "~common/components/json-label-fc"

registerJsonLabel()

// Sets up context to be consumed by the field components via provider
export const ValueContext = createContext();


interface FieldRendererProps {
  fieldDefinition: any;
  error?: any;
  value?: any;
  onCommitChange?: any;
  path?: string;
  policyMessages?: any;
  readOnly?: boolean;
  onAccessRootData?: any;
  triggerAction?: Function
  triggerValidationAtURLFunc?: Function
  onKeyDown?: any;
  onBeforeTriggerValidation?: () => void;
  onAfterTriggerValidation?: () => void;
}

export const Field: React.FC<FieldRendererProps> = ({
  fieldDefinition,
  policyMessages,
  value,
  onCommitChange,
  path,
  readOnly,
  onAccessRootData,
  onKeyDown,
  onBeforeTriggerValidation,
  onAfterTriggerValidation,
  triggerAction,
  triggerValidationAtURLFunc
  //TO BE IMPLEMENTED: 
  //onFileReject
  //onValidate
  //customRenderers
  //triggerAction
}) => {

  // Helper Functions


  // Rendering Code
  const [fieldValue, setFieldValue] = useState(value || fieldDefinition.defaultValue);  
  // const [dataValue, setDataValue] = useState(data);  

  var fieldMessages = filterPolicyMessages(policyMessages, path);

  useEffect(() => {
    var suppressTriggerValidation = false;
    if (fieldDefinition.triggerValidationAtURL && fieldDefinition.triggerValidationAtURLAttributes && fieldDefinition.triggerValidationAtURLAttributes.suppressOnBlur) {
      suppressTriggerValidation = fieldDefinition.triggerValidationAtURLAttributes.suppressOnBlur;
    }
    if (!isEqual(value, fieldValue)) { 
      // don't commit a change if the old value and new value are both falsy
      // Except if the new value is 0
      if (value || fieldValue || fieldValue === 0) { 
        if (value !== fieldValue) {
          // We do not want to commit a change if the value has not changed 
          // since this also clears policy messages
          if (onCommitChange) {
            onCommitChange(fieldValue, suppressTriggerValidation);
          }
        }
      }
    }
  }, [fieldValue])

  // Looks for changes from the parent def
  useEffect(() => {
    if (!isEqual(value, fieldValue)) {
      setFieldValue(value);
    }
  }, [value])


  if (fieldDefinition.mode && fieldDefinition.mode === "Hidden") {
    return null
  }

  switch (fieldDefinition.type) {
    case "Text":
      if (!fieldDefinition.variant || fieldDefinition.variant.toLowerCase() != "question") {
        return (
          <ValueContext.Provider value={[fieldValue, setFieldValue]}>
            <TextField
              fieldDefinition={fieldDefinition}
              readOnlyOverride={readOnly}
              policyMessages={fieldMessages}
              path={path} 
              onAccessRootData={onAccessRootData}
              triggerValidationAtURLFunc={fieldDefinition.triggerValidationAtURL ? onCommitChange:null}
            />
          </ValueContext.Provider>
        );
      }
      // Fallthrough to question Field
    case "Autocomplete":
    case "Relation":
    case "RelationList":
      if (!fieldDefinition.variant || fieldDefinition.variant.toLowerCase() != "question") {
        return (
          <ValueContext.Provider value={[fieldValue, setFieldValue]}>
            <AutocompleteField
              path={path}
              fieldDefinition={fieldDefinition}
              policyMessages={fieldMessages}
              readOnlyOverride={readOnly}
              onAccessRootData={onAccessRootData}
            />
          </ValueContext.Provider>
        );
      }
      // Also fallthrough to question field
    
    case "Question":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <QuestionField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "Bool":
    case "Boolean":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <BooleanField
            fieldDefinition={fieldDefinition}
            policyMessages={fieldMessages}
            readOnlyOverride={readOnly}
          />
        </ValueContext.Provider>
      );

    case "Numeric":
    case "Number":
    case "Currency":
      return (      
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <NumberField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            onKeyDown={onKeyDown}
            path={path}            
          />
        </ValueContext.Provider>
      )

    case "Date":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <DateField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            path={path}
          />
        </ValueContext.Provider>
      );

    case "DateTime":
    case "Time":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <TimeField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "Checkbox":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <CheckboxField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "VersionedDocument":

      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <VersionedDocumentField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "VersionedDocumentList":
      
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <VersionedDocumentListField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "ImageList":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <ImageList
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            path={path} 
          />
        </ValueContext.Provider>
      );

    case "DocumentList":
      return <div>DocumentList Unimplemented</div>

    case "NumericRange":
      return ( 
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <NumberRangeField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      )

    case "DateRange":   
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <DateRangeField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "TimeRange":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <TimeRangeField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
          />
        </ValueContext.Provider>
      );

    case "Address":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <AddressField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            path={path} 
            onAccessRootData={onAccessRootData}
          />
        </ValueContext.Provider>
      );
    case "Validation":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <ValidationField
            fieldDefinition={fieldDefinition}
            path={path} 
            onAccessRootData={onAccessRootData}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            onBeforeTriggerValidation={onBeforeTriggerValidation}
            onAfterTriggerValidation={onAfterTriggerValidation}
          />
        </ValueContext.Provider>
      );

    case "Json":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <JsonField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            path={path} 
          />
      </ValueContext.Provider>
      )
    case "RichText":
      return (
        <ValueContext.Provider value={[fieldValue, setFieldValue]}>
          <RichTextField
            fieldDefinition={fieldDefinition}
            readOnlyOverride={readOnly}
            policyMessages={fieldMessages}
            path={path} 
          />
        </ValueContext.Provider>
      )
    default:
      
      return (
        <div>Missing Field Definition Type</div>
      )
  }

}

export function EvaluateDateLimit(dt: string): any {
  try {
    var dur: any;
    var regex = /([+-]?)\s*(\d+)\s*(\w+)+/;
    var parts = dt.toLowerCase().match(regex);

    if (parts && parts.length > 3) {
      const sign = parts[1];
      const qty = parts[2];
      const unit = parts[3];

      let val = parseInt(qty);
      if (sign == "-") {
        val = -1 * val;
      }

      switch (unit) {
        case "d":
        case "day":
        case "days":
          dur = moment.duration(val, "days");
          break;

        case "w":
        case "week":
        case "weeks":
          dur = moment.duration(val, "weeks");
          break;

        case "m":
        case "month":
        case "months":
          dur = moment.duration(val, "months");
          break;

        case "y":
        case "year":
        case "years":
          dur = moment.duration(val, "years");
          break;
      }
    }

    const limitDt = moment(new Date()).add(dur).toDate();
    return limitDt;
  } catch (e) {
    console.error(e);
  }
}

export default Field;